/**
 * Builds the POST /workwise/deployments/ payload (contract section 3) from the
 * wizard form values.
 *
 * The DB and the gateway both take their mode from the ONE deployment model
 * (revision 3), so the payload can never carry a mixed topology, and a
 * dedicated DB / gateway always carries the step-2 size (R3-G). Only the active
 * variant of each server is emitted: in shared mode the dedicated-only keys are
 * ABSENT (the JSON schema is additionalProperties:false), never null. Optional
 * fields that are empty are omitted too. No network NAMES (vnet/subnet) are
 * sent: the backend derives them. Revision 4 adds the top-level `network`:
 * Dedicated always sends its two address ranges; SaaS-Hybrid sends
 * `existingNetworkId` only when one is chosen, and otherwise no `network` at all.
 * No secret values exist in the form; references pass through unchanged.
 * Revision 6: no admin credential is sent (no `appServer.adminAccess`); a
 * password is generated for each VM during deployment. Revision 8: no gateway
 * host name or TLS certificate reference in either mode. A dedicated DB is always SQL
 * Server 2022; it sends its SQL authentication mode (the login only for 'sql', and never a
 * password: the pipeline generates it), the storage workload and the role-tagged `disks`
 * (data, then log, then tempdb disks; none for a TempDB on the local SSD). With `skipApplication`
 * (the Application (ERP) step skipped: infrastructure only) the payload has no `app` key.
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { toAzureLocation } from '../../config/azureRegions'
import { DB_ENGINE_VERSION } from '../../config/workwise'
import type {
  DiskFormValue,
  PayloadDbDedicated,
  PayloadDbShared,
  PayloadDisk,
  PayloadGatewayDedicated,
  PayloadGatewayShared,
  PayloadNetwork,
  ServerMode,
  WorkwiseFormValues,
  WorkwisePayload,
  ZoneChoice,
  ZoneValue,
} from '../../types/workwise'
import { flattenDbDisks } from './dbStorage'
import { serverModeOf } from './model'
import { payloadSchema } from './schema'

export interface BuildPayloadInput {
  values: WorkwiseFormValues
  /** A real UUID v4, generated once per wizard session. */
  correlationId: string
  /** From estimateCost().total. */
  estimatedMonthlyCost: number
  /** The standard Deploy dialog's choice: "Submit for Approval" (with notes) or "Deploy Directly". */
  approval?: { required: boolean; notes: string }
}

/** The backend accepts at most this many characters of approval notes. */
export const APPROVAL_NOTES_MAX = 2000

const text = (value: string) => value.trim()

function zoneValue(zone: ZoneChoice): ZoneValue {
  return zone === '' ? null : zone
}

/** The disk list of the app server and the gateway. */
function payloadDisks(disks: DiskFormValue[]): PayloadDisk[] {
  return disks.map((d) => ({ sizeGb: Number(d.sizeGb), type: d.type }))
}

function buildDb(values: WorkwiseFormValues, mode: ServerMode): PayloadDbShared | PayloadDbDedicated {
  const db = values.dbServer

  // No databaseName: it is optional and Ansible picks it when it sets up the application.
  if (mode === 'shared') {
    return { mode: 'shared', sharedServerId: Number(db.sharedServerId) }
  }
  return {
    mode: 'dedicated',
    // R3-G: the size chosen in Configure Sizes, like the app server.
    sku: values.appServer.sku,
    vmName: text(db.vmName),
    engine: 'sqlserver',
    engineVersion: DB_ENGINE_VERSION,
    sqlAuthentication: db.sqlAuthentication,
    // The login exists only for SQL Server authentication; a 'windows' DB sends no key at all.
    ...(db.sqlAuthentication === 'sql' ? { sqlLogin: text(db.sqlLogin) } : {}),
    storageWorkload: db.storageWorkload,
    disks: flattenDbDisks(db.storage),
    zone: zoneValue(db.zone),
  }
}

function buildGateway(values: WorkwiseFormValues, mode: ServerMode): PayloadGatewayShared | PayloadGatewayDedicated {
  const gw = values.gatewayServer
  if (mode === 'shared') {
    return { mode: 'shared', sharedServerId: Number(gw.sharedServerId) }
  }
  return {
    mode: 'dedicated',
    sku: values.appServer.sku,
    vmName: text(gw.vmName),
    publicIp: gw.publicIp,
    disks: payloadDisks(gw.disks),
    nsgRules: gw.nsgRules.map((r) => ({ name: text(r.name), port: Number(r.port), source: text(r.source) })),
    zone: zoneValue(gw.zone),
  }
}

/** R4-A: the ranges of a Dedicated network, or the existing network a SaaS-Hybrid app server joins (if any). */
function buildNetwork(values: WorkwiseFormValues, mode: ServerMode): PayloadNetwork | undefined {
  const network = values.network
  if (mode === 'dedicated') return { vnetCidr: text(network?.vnetCidr ?? ''), subnetCidr: text(network?.subnetCidr ?? '') }
  const id = network?.existingNetworkId
  return id != null && Number.isInteger(Number(id)) ? { existingNetworkId: Number(id) } : undefined
}

/** The Application (ERP) settings of step 4. */
function buildApp(app: WorkwiseFormValues['app']): NonNullable<WorkwisePayload['app']> {
  return {
    version: text(app.version),
    tenantName: text(app.tenantName),
    licenseKeyRef: text(app.licenseKeyRef),
    smtp: {
      host: text(app.smtp.host),
      port: Number(app.smtp.port),
      username: app.smtp.username,
      passwordRef: text(app.smtp.passwordRef),
      fromAddress: text(app.smtp.fromAddress),
      useTls: app.smtp.useTls,
    },
    dbConnectionRef: text(app.dbConnectionRef),
  }
}

export function buildPayload({ values, correlationId, estimatedMonthlyCost, approval }: BuildPayloadInput): WorkwisePayload {
  const { customer, appServer, app, tags, skipApplication } = values
  // One mode for both: SaaS-Hybrid = shared + shared, Dedicated = dedicated + dedicated.
  const mode = serverModeOf(values.deploymentModel)
  const network = buildNetwork(values, mode)

  const extraEntries = tags.extra
    .map((t) => [text(t.key), text(t.value)] as const)
    .filter(([key]) => key !== '')
  const extra = extraEntries.length > 0 ? Object.fromEntries(extraEntries) : undefined
  // Sent only for "Submit for Approval"; "Deploy Directly" leaves both keys out (backend default: false).
  const approvalNotes = approval?.required ? text(approval.notes).slice(0, APPROVAL_NOTES_MAX) : ''

  return {
    customer: { name: text(customer.name), number: customer.number, slug: customer.slug },
    environment: values.environment,
    region: toAzureLocation(values.region),
    cloudProvider: 'azure',
    cicd: values.cicd,
    appServer: {
      sku: appServer.sku,
      vmName: text(appServer.vmName),
      os: appServer.os,
      osDiskType: appServer.osDiskType,
      disks: payloadDisks(appServer.disks),
      zone: zoneValue(appServer.zone),
    },
    dbServer: buildDb(values, mode),
    gatewayServer: buildGateway(values, mode),
    ...(network ? { network } : {}),
    // Skipped Application (ERP) step: no `app` key at all (infrastructure only).
    ...(skipApplication ? {} : { app: buildApp(app) }),
    tags: {
      owner: text(tags.owner),
      ...(extra ? { extra } : {}),
    },
    estimatedMonthlyCost,
    correlationId,
    ...(approval?.required ? { approvalRequired: true, ...(approvalNotes ? { approvalNotes } : {}) } : {}),
  }
}

/**
 * Parses a payload against the strict outgoing schema. Returns the issues as a
 * flat { "dotted.path": ["message"] } map (never echoing input values), or null
 * when the payload is valid.
 */
export function validatePayload(payload: unknown): Record<string, string[]> | null {
  const result = payloadSchema.safeParse(payload)
  if (result.success) return null
  const errors: Record<string, string[]> = {}
  for (const issue of result.error.issues) {
    const path = issue.path.join('.') || 'non_field_errors'
    ;(errors[path] ??= []).push(issue.message)
  }
  return errors
}
